import * as Yup from 'yup';
import { INDUCED, WITHOUT } from '../../../../../constants/laborModality';
import { SPONTANEOUS } from '../../../../../constants/childbirthModality';
import {
  SCHEDULED_CESAREAN,
  URGENT_CESAREAN,
  VAGINAL,
} from '../../../../../constants/newChildbirthModality';
import { NON_MEDICAL_INDICATION } from '../../../../../constants/preLaborCesareanReasons';

import SectionBFields from '../../../../../assets/sectionBFields.json';
import { presentationOptions } from '../../../../../constants/options';

const newbornVitalityBaseSchema = Yup.object().shape({
  gender: Yup.string().oneOf(['male', 'female']).required('Campo obbligatorio'),
  vitalityCheckbox: Yup.boolean()
    .transform((value) => (value === '' ? null : value))
    .nullable(),
  vitality: Yup.string().oneOf(['born_alive', 'stillborn']).required('Campo obbligatorio'),
  malformation: Yup.boolean().when('vitality', {
    is: 'stillborn',
    then: (schema) => schema.required('Campo obbligatorio'),
    otherwise: (schema) => schema.transform((value) => (value === '' ? null : value)).nullable(),
  }),
  childbirthModality: Yup.string()
    .oneOf(Object.keys(SectionBFields.childbirthModality.options))
    // eslint-disable-next-line func-names
    .test('childbirth-modality-validation', 'Campo obbligatorio', function (value) {
      const { from } = this;
      const { value: childbirthData } = from[1] ?? {};
      const { laborModality, preLaborCesareanReason } = childbirthData ?? {};

      if (
        laborModality === WITHOUT.id &&
        preLaborCesareanReason === NON_MEDICAL_INDICATION.id &&
        value === URGENT_CESAREAN.id
      )
        return this.createError({
          message:
            'Un TC per indicazione non medica (elettivo) non può essere classificato come urgente.',
        });
      if (laborModality === WITHOUT.id && value === VAGINAL.id)
        return this.createError({
          message: 'Parto vaginale non è possibile in assenza di travaglio.',
        });

      if ([SPONTANEOUS.id, INDUCED.id].includes(laborModality)) {
        if (value === SCHEDULED_CESAREAN.id)
          return this.createError({
            message: 'Non è possibile un TC programmato se il travaglio è già iniziato.',
          });
      }

      return true;
    })
    .required('Campo obbligatorio'),
  childbirthOrder: Yup.number().min(1, 'Deve essere almeno 1').required('Campo obbligatorio'),
  presentation: Yup.string().oneOf(Object.keys(presentationOptions)).required('Campo obbligatorio'),
  kristellerManeuver: Yup.boolean().when('childbirthModality', {
    is: (val) => [VAGINAL.id, URGENT_CESAREAN.id].includes(val),
    then: (schema) => schema.required('Campo obbligatorio'),
    otherwise: (schema) => schema.transform((value) => (value === '' ? null : value)).nullable(),
  }),
  vacuumExtraction: Yup.boolean().when('childbirthModality', {
    is: (val) => [VAGINAL.id, URGENT_CESAREAN.id].includes(val),
    then: (schema) => schema.required('Campo obbligatorio'),
    otherwise: (schema) => schema.transform((value) => (value === '' ? null : value)).nullable(),
  }),
  forceps: Yup.boolean().when('childbirthModality', {
    is: (val) => [VAGINAL.id, URGENT_CESAREAN.id].includes(val),
    then: (schema) => schema.required('Campo obbligatorio'),
    otherwise: (schema) => schema.transform((value) => (value === '' ? null : value)).nullable(),
  }),
  anesthesia: Yup.string()
    .transform((value) => (value == null || value === '' ? undefined : value))
    .oneOf(Object.keys(SectionBFields.tc_anesthesia.options))
    .when('childbirthModality', {
      is: (val) => [SCHEDULED_CESAREAN.id, URGENT_CESAREAN.id].includes(val),
      then: (schema) => schema.required('Campo obbligatorio'),
      otherwise: (schema) => schema.optional(),
    }),
});

const newbornVitalitySpontaneousSchema = newbornVitalityBaseSchema.concat(
  Yup.object().shape({
    episiotomy: Yup.boolean()
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .when('childbirthModality', {
        is: VAGINAL.id,
        then: (schema) => schema.required('Campo obbligatorio'),
      }),
    severeLacerations: Yup.string()
      .transform((value) => (value == null || value === '' ? undefined : value))
      .oneOf(Object.keys(SectionBFields.severeLacerations.options), 'Valore non valido')
      .when('childbirthModality', {
        is: VAGINAL.id,
        then: (schema) => schema.required('Campo obbligatorio'),
      }),
    inLaborCesareanReason: Yup.string()
      .transform((value) => (value == null || value === '' ? undefined : value))
      .oneOf(Object.keys(SectionBFields.inLaborCesareanReasons.options), 'Valore non valido')
      .when('childbirthModality', {
        is: URGENT_CESAREAN.id,
        then: (schema) => schema.required('Campo obbligatorio'),
      }),
  })
);

const newbornVitalityInducedSchema = newbornVitalityBaseSchema.concat(
  Yup.object().shape({
    episiotomy: Yup.boolean()
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .when('childbirthModality', {
        is: VAGINAL.id,
        then: (schema) => schema.required('Campo obbligatorio'),
      }),
    severeLacerations: Yup.string()
      .transform((value) => (value == null || value === '' ? undefined : value))
      .oneOf(Object.keys(SectionBFields.severeLacerations.options), 'Valore non valido')
      .when('childbirthModality', {
        is: VAGINAL.id,
        then: (schema) => schema.required('Campo obbligatorio'),
      }),
    inLaborCesareanReason: Yup.string()
      .transform((value) => (value == null || value === '' ? undefined : value))
      .oneOf(Object.keys(SectionBFields.inLaborCesareanReasons.options), 'Valore non valido')
      .when('childbirthModality', {
        is: URGENT_CESAREAN.id,
        then: (schema) => schema.required('Campo obbligatorio'),
      }),
  })
);

const newbornVitalityWithoutSchema = newbornVitalityBaseSchema;

export default Yup.object().shape({
  childbirthData: Yup.object().shape({
    place: Yup.string()
      .oneOf(Object.keys(SectionBFields.childbirthPlace.options))
      .required('Campo obbligatorio'),
    laborModality: Yup.string()
      .oneOf(Object.keys(SectionBFields.laborModality.options))
      .required('Campo obbligatorio'),
    inductionTypes: Yup.array()
      .nullable()
      .transform((value) => (value == null ? [] : value))
      .of(Yup.string().oneOf(Object.keys(SectionBFields.inductionTypes.options)))
      .when('laborModality', {
        is: INDUCED.id,
        then: (schema) => schema.required('Campo obbligatorio').min(1, 'Campo obbligatorio'),
      }),
    inductionReason: Yup.string()
      .nullable()
      .when('laborModality', {
        is: INDUCED.id,
        then: (schema) =>
          schema
            .oneOf(Object.keys(SectionBFields.inductionReasons.options))
            .required('Campo obbligatorio'),
      }),
    acceleration: Yup.string()
      .nullable()
      .when('laborModality', {
        is: SPONTANEOUS.id,
        then: (schema) =>
          schema
            .oneOf(Object.keys(SectionBFields.accelerators.options))
            .required('Campo obbligatorio'),
      }),
    laborManagement: Yup.string()
      .nullable()
      .when('laborModality', {
        is: (val) => [INDUCED.id, SPONTANEOUS.id].includes(val),
        then: (schema) =>
          schema
            .oneOf(Object.keys(SectionBFields.laborManagement.options))
            .required('Campo obbligatorio'),
      }),
    freePositionsDuringLabor: Yup.boolean().when('laborModality', {
      is: (val) => [INDUCED.id, SPONTANEOUS.id].includes(val),
      then: (schema) => schema.required('Campo obbligatorio'),
      otherwise: (schema) => schema.transform((value) => (value === '' ? null : value)).nullable(),
    }),
    analgesiaDetailsArray: Yup.array()
      .of(Yup.string().oneOf(Object.keys(SectionBFields.analgesia.options)))
      .when('laborModality', {
        is: (val) => [INDUCED.id, SPONTANEOUS.id].includes(val),
        then: (schema) => schema.min(1, 'Campo obbligatorio'),
        otherwise: (schema) => schema.nullable(),
      }),
    preLaborCesareanReason: Yup.string()
      .nullable()
      .when('laborModality', {
        is: WITHOUT.id,
        then: (schema) =>
          schema
            .oneOf(Object.keys(SectionBFields.preLaborCesareanReasons.options))
            .required('Campo obbligatorio'),
      }),
    childbirthKind: Yup.string().oneOf(['simple', 'multiple']).required('Campo obbligatorio'),
    males: Yup.number()
      .transform((v) => (v === '' ? 0 : v))
      .when('childbirthKind', {
        is: 'simple',
        then: (schema) => schema.nullable(),
      })
      .min(0)
      .max(9)
      .test(
        'newborn-count',
        'La somma di numero nati maschi e numero nate femmine deve essere maggiore di 1.',
        // eslint-disable-next-line func-names
        function () {
          const { males, females, childbirthKind } = this.parent;
          if (childbirthKind === 'simple') return true;
          if ((males ?? 0) + (females ?? 0) <= 1) return false;
          return true;
        }
      ),
    females: Yup.number()
      .transform((v) => (v === '' ? 0 : v))
      .when('childbirthKind', {
        is: 'simple',
        then: (schema) => schema.nullable(),
      })
      .min(0)
      .max(9)
      .test(
        'newborn-count',
        'La somma di numero nati maschi e numero nate femmine deve essere maggiore di 1.',
        // eslint-disable-next-line func-names
        function () {
          const { males, females, childbirthKind } = this.parent;
          if (childbirthKind === 'simple') return true;
          if ((males ?? 0) + (females ?? 0) <= 1) return false;
          return true;
        }
      ),
    attendantHCP: Yup.array()
      .of(Yup.string().oneOf(Object.keys(SectionBFields.attendantHCPs.options)))
      .min(1, 'Campo obbligatorio'),
    presenceBirthRoom: Yup.string()
      .oneOf(Object.keys(SectionBFields.birthRoomPresence.options))
      .required('Campo obbligatorio'),
    newbornVitality: Yup.array()
      .when('laborModality', {
        is: SPONTANEOUS.id,
        then: (schema) => schema.of(newbornVitalitySpontaneousSchema),
        otherwise: (schema) =>
          schema.when('laborModality', {
            is: INDUCED.id,
            then: (s) => s.of(newbornVitalityInducedSchema),
            otherwise: (s) =>
              s.when('laborModality', {
                is: WITHOUT.id,
                then: (sc) => sc.of(newbornVitalityWithoutSchema),
                otherwise: (sc) => sc.of(newbornVitalityBaseSchema),
              }),
          }),
      })
      .min(1, 'Campo obbligatorio'),
    genderValidation: Yup.string().test(
      'gender-count-validation',
      'Il numero di maschi e femmine selezionati deve corrispondere ai valori inseriti.',
      // eslint-disable-next-line func-names
      function () {
        const { childbirthKind, males, females, newbornVitality = [] } = this.parent;
        if (childbirthKind === 'simple') return true;
        // Invalid array data is reported by the field schema, not by this cross-field check.
        if (
          !Array.isArray(newbornVitality) ||
          newbornVitality.some((newborn) => !newborn || typeof newborn !== 'object')
        )
          return true;

        const maleCount = newbornVitality.filter((n) => n.gender === 'male').length;
        const femaleCount = newbornVitality.filter((n) => n.gender === 'female').length;

        const expectedMales = males ?? 0;
        const expectedFemales = females ?? 0;

        if (maleCount !== expectedMales || femaleCount !== expectedFemales) {
          return this.createError({
            message: `Sono stati selezionati ${maleCount} maschi e ${femaleCount} femmine, ma i campi indicano ${expectedMales} maschi e ${expectedFemales} femmine.`,
          });
        }

        return true;
      }
    ),
  }),
});
